import { Fragment, useState, type FormEvent } from 'react';
import { get, post } from '../api';
import { ContactSelect } from '../components/ContactSelect';
import { ComposeButtons, inviteLinks, type InviteCompose } from '../components/ComposeButtons';
import { CopyLink } from '../components/CopyLink';
import { Field, Problems } from '../components/Field';
import { toast } from '../components/toast';
import { describe } from '../errors';
import { useLoad } from '../hooks';
import { AccessPill, RowActions, day, useAccessActions, type Access } from './userAccess';

interface PortalUser {
  id: number;
  email: string;
  contact_id: number | null;
  contact_name: string | null;
  state: string;
  access: Access;
  has_password: boolean;
  invited_at: string | null;
  last_login_at: string | null;
}

interface Sent {
  emailed?: boolean;
  link?: string;
  compose?: InviteCompose;
}

/** The Portal users tab of the Users page: who can sign in to the portal (contractors, and later customers). They are invited by email and set their own password. */
export function PortalUsers() {
  const [tick, setTick] = useState(0);
  const [contact, setContact] = useState(0);
  const [email, setEmail] = useState('');
  /** Once they type an address, choosing a person no longer overwrites it. */
  const [emailEdited, setEmailEdited] = useState(false);
  const [problems, setProblems] = useState<string[]>([]);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [message, setMessage] = useState('');
  /** Set when nobody was emailed (email is off or failed), so the office can pass the link on. */
  const [link, setLink] = useState('');
  /** The invite ready to send from this device, when it is set to go by hand or could not go. */
  const [compose, setCompose] = useState<InviteCompose | undefined>();
  const list = useLoad(() => get<{ items: PortalUser[] }>('/api/portal-users'), [tick]);
  const actions = useAccessActions(
    '/api/portal-users',
    () => setTick((t) => t + 1),
    () => {
      setLink('');
      setCompose(undefined);
      setProblems([]);
      setFields({});
      setMessage('');
    },
  );

  async function invite(e: FormEvent) {
    e.preventDefault();
    setProblems([]);
    setFields({});
    setMessage('');
    setLink('');
    setCompose(undefined);
    actions.setLink(null);
    actions.setProblems([]);
    try {
      const r = await post<Sent>('/api/portal-users', { contact_id: contact, email });
      toast(r.emailed ? `Invitation sent to ${email}.` : `Account created for ${email}.`);
      setLink(r.link ?? '');
      setCompose(r.compose);
      setEmail('');
      setEmailEdited(false);
      setContact(0);
      setTick((t) => t + 1);
    } catch (err) {
      const d = describe(err);
      setProblems(d.problems);
      setFields(d.fields);
    }
  }

  /** Look at the portal as this contractor, read-only, in a new tab. */
  async function viewAs(id: number) {
    setProblems([]);
    setMessage('');
    setLink('');
    setCompose(undefined);
    actions.setLink(null);
    actions.setProblems([]);
    // Opened now, inside the click, so the browser lets it through; pointed at the portal once the office has checked it.
    const tab = window.open('', '_blank');
    if (!tab) {
      setMessage('Your browser blocked the new tab. Allow pop-ups for this site and try again.');
      return;
    }
    try {
      await post(`/api/portal-users/${id}/view-as`);
      tab.opener = null;
      tab.location.href = `/portal?as=${id}`;
    } catch (err) {
      tab.close();
      setProblems(describe(err).problems);
    }
  }

  const items = list.data?.items ?? [];
  return (
    <>
      <p className="muted">
        Who can sign in to the contractor portal. Each is invited by email and sets their own
        password.
      </p>
      <form onSubmit={invite} className="stack">
        <fieldset>
          <legend>Invite someone</legend>
          <Field label="Person" error={fields.contact_id}>
            <ContactSelect
              role="contractor"
              value={contact}
              onChange={setContact}
              exclude={items.map((i) => i.contact_id).filter((c): c is number => c !== null)}
              onPick={(o) => {
                if (!emailEdited) setEmail(o?.email ?? '');
              }}
            />
          </Field>
          <Field label="Their email" error={fields.email} hint="We will send the invitation here.">
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setEmailEdited(true);
              }}
              required
            />
          </Field>
          <button type="submit" disabled={contact === 0 || email.trim() === ''}>
            Send invitation
          </button>
        </fieldset>
      </form>
      {message && (
        <p className="notice" role="status">
          {message}
        </p>
      )}
      {link && <CopyLink key={link} link={link} />}
      <ComposeButtons items={inviteLinks(compose)} />
      <Problems problems={[...problems, ...actions.problems]} title="Could not do that." focus />
      {list.error && (
        <p role="alert" className="error">
          {list.error}
        </p>
      )}
      {list.loading && !list.data && <p className="muted">Loading…</p>}
      {!list.loading && !list.error && items.length === 0 && (
        <p className="empty">No one has portal access yet. Invite your first contractor above.</p>
      )}
      {items.length > 0 && (
        <>
          <h2>People with portal access</h2>
          <table className="people">
            <caption className="sr-only">People with portal access</caption>
            <thead>
              <tr>
                <th scope="col">Person</th>
                <th scope="col">Email</th>
                <th scope="col">Status</th>
                <th scope="col">Last sign-in</th>
                <th scope="col">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => {
                const person = { ...u, name: u.contact_name || u.email };
                return (
                  <Fragment key={u.id}>
                    <tr>
                      <td data-label="Person">{u.contact_name || '—'}</td>
                      <td data-label="Email">{u.email}</td>
                      <td data-label="Status">
                        <AccessPill access={u.access} invitedAt={u.invited_at} />
                      </td>
                      <td data-label="Last sign-in">
                        {u.access === 'invited' || u.access === 'expired'
                          ? '—'
                          : day(u.last_login_at)}
                      </td>
                      <td className="actions">
                        <RowActions
                          person={person}
                          actions={actions}
                          canSwitchOff
                          onView={
                            u.access !== 'off' && u.contact_id !== null
                              ? () => void viewAs(u.id)
                              : undefined
                          }
                        />
                      </td>
                    </tr>
                    {actions.link?.id === u.id && (
                      <tr className="link-row">
                        <td colSpan={5}>
                          {actions.link.url && (
                            <CopyLink
                              key={actions.link.url}
                              link={actions.link.url}
                              purpose={actions.link.purpose}
                            />
                          )}
                          <ComposeButtons items={inviteLinks(actions.link.compose)} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </>
  );
}
